import { useCallback, useMemo, useState } from 'react';
import { Button, Modal, Space, Typography, message } from 'antd';
import { DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Row selection and a bulk delete, for the admin panel's inbox-style lists.
 *
 * One hook rather than the same forty lines in three pages: the confirmation
 * wording, the "selected rows went missing" case and the clearing of the
 * selection afterwards are each easy to get subtly different, and a delete
 * that behaves differently on three screens is a delete nobody trusts.
 *
 * @param deleteMany  async (ids) => { deleted, missing }
 * @param onDone      called after a successful delete, to reload the table
 * @param label       singular noun for the confirmation, e.g. "subscriber"
 */
export default function useBulkDelete({ deleteMany, onDone, label = 'row' }) {
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);

    const clear = useCallback(() => setSelected([]), []);

    const run = useCallback(async (ids) => {
        setBusy(true);
        try {
            const res = await deleteMany(ids);
            const deleted = res?.deleted ?? ids.length;
            // A shortfall means somebody else removed them first. Worth
            // saying — the operator selected rows that no longer existed —
            // but not worth treating as a failure.
            message.success(
                res?.missing > 0
                    ? `${deleted} deleted. ${res.missing} had already been removed.`
                    : `${deleted} ${label}${deleted === 1 ? '' : 's'} deleted.`,
            );
            clear();
            if (onDone) await onDone();
        } catch (e) {
            message.error(e?.response?.data?.message || `Could not delete the selected ${label}s.`);
        } finally {
            setBusy(false);
        }
    }, [deleteMany, onDone, label, clear]);

    const confirmAndDelete = useCallback((ids) => {
        const n = ids.length;
        Modal.confirm({
            title: `Delete ${n} ${label}${n === 1 ? '' : 's'}?`,
            icon: <ExclamationCircleFilled />,
            // Said plainly because it is true and there is no undo.
            content: 'This cannot be undone.',
            okText: 'Delete',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => run(ids),
        });
    }, [label, run]);

    const rowSelection = useMemo(() => ({
        selectedRowKeys: selected,
        onChange: setSelected,
        // Ant's own "select all" only covers the page in view. Spelled out in
        // the toolbar below, so nobody presses delete believing it covered
        // every page.
        preserveSelectedRowKeys: false,
    }), [selected]);

    const toolbar = selected.length > 0 ? (
        <Space style={{ marginBottom: 12 }}>
            <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                loading={busy}
                onClick={() => confirmAndDelete(selected)}
            >
                Delete {selected.length} selected
            </Button>
            <Button onClick={clear} disabled={busy}>Clear selection</Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
                Selection covers this page only.
            </Text>
        </Space>
    ) : null;

    return { rowSelection, toolbar, selected, clear, confirmAndDelete, busy };
}
